import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { CircleOff } from "lucide-react-native";
import Text from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { CancerTreatmentDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";
import { CANCER_TREATMENT_OPTIONS } from "../options";
import type { CancerTreatmentType } from "../options";
import { useMedicalHistoryTheme } from "../../../../theme/MedicalHistoryTheme";

export function CancerTreatmentsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const theme = useMedicalHistoryTheme();
  const progress = useFlowProgress("CancerTreatments");

  // For now, treatments are managed on the first cancer entry.
  // If multiple cancers exist, this screen shows per-cancer sections.
  const cancers = state.cancers;

  const toggleTreatment = (
    cancerIndex: number,
    treatmentType: CancerTreatmentType,
  ) => {
    const cancer = cancers[cancerIndex]!;
    const existing = cancer.treatments.find(
      (treatment) => treatment.treatmentType === treatmentType,
    );

    const updatedCancers = cancers.map((c, i) => {
      if (i !== cancerIndex) return c;
      if (existing) {
        const idsToDelete = existing.id
          ? [...c.treatmentIdsToDelete, existing.id]
          : c.treatmentIdsToDelete;
        return {
          ...c,
          treatments: c.treatments.filter(
            (treatment) => treatment.treatmentType !== treatmentType,
          ),
          treatmentIdsToDelete: idsToDelete,
        };
      } else {
        const newTreatment: CancerTreatmentDetail = { treatmentType };
        return {
          ...c,
          treatments: [...c.treatments, newTreatment],
        };
      }
    });

    updateState({ cancers: updatedCancers });
  };

  const handleNone = (cancerIndex: number) => {
    const cancer = cancers[cancerIndex]!;
    const idsToDelete = cancer.treatments
      .filter((treatment) => treatment.id)
      .map((treatment) => treatment.id!);
    const updatedCancers = cancers.map((c, i) => {
      if (i !== cancerIndex) return c;
      return {
        ...c,
        treatments: [],
        treatmentIdsToDelete: [...c.treatmentIdsToDelete, ...idsToDelete],
      };
    });
    updateState({ cancers: updatedCancers });
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("CancerTreatments");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.disease_history.cancer_treatments.title")}
      subtitle={t(
        "med_history.medical.disease_history.cancer_treatments.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        {cancers.map((cancer, cancerIndex) => {
          const selectedTreatments = new Set(
            cancer.treatments.map((treatment) => treatment.treatmentType),
          );
          const isNone = cancer.treatments.length === 0;

          return (
            <View key={cancerIndex}>
              {cancers.length > 1 && (
                <Text
                  style={{ color: theme.cardLabel }}
                  className="mb-3 pl-1 text-sm font-medium tracking-wide"
                >
                  {cancer.cancer
                    ? cancer.cancer.toUpperCase()
                    : t(
                        "med_history.medical.disease_history.cancer_treatments.cancer_label",
                        { params: { index: String(cancerIndex + 1) } },
                      )}
                </Text>
              )}
              <View style={styles.grid}>
                {CANCER_TREATMENT_OPTIONS.map((option) => (
                  <View key={option.id} style={styles.cell}>
                    <SquareOptionButton
                      icon={option.icon}
                      label={t(option.labelKey)}
                      isSelected={selectedTreatments.has(option.id)}
                      onPress={() => toggleTreatment(cancerIndex, option.id)}
                      labelSize="sm"
                    />
                  </View>
                ))}
                <View style={styles.cell}>
                  <SquareOptionButton
                    icon={CircleOff}
                    label={t("med_history.medical.shared.none")}
                    isSelected={isNone}
                    onPress={() => handleNone(cancerIndex)}
                    labelSize="sm"
                  />
                </View>
              </View>
            </View>
          );
        })}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 24,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
